import { FilterTagsStyled } from './styles/FilterTagsStyled'
import { TextButton } from '../../elements/Button'
import { type IconElement, IconX } from '../../elements/Icon'
import { Tag } from '../../elements/Tag'
import { SubText } from '../../elements/Text'
import { WithTooltip } from '../../elements/Tooltip'
import { ControlledList, type ControlledListContext } from '../ControlledList'
import { Space } from '@ui/elements/Space'

export interface BaseFilterTag {
  label: string
  tooltip?: string
  showTooltip?: boolean
  color?: string
  bgcolor?: string
  locked?: boolean
  readonly?: boolean
}

export interface FilterTag<TagValue = any> extends BaseFilterTag {
  key: string
  value: TagValue
}

export interface IFilterTagsProps {
  /** Icon for the remove button for each tag */
  removeIcon?: IconElement
  /** Array of tags */
  tags?: FilterTag[] | string[]
  fullWidthTags?: boolean
  /** Background color of each tag */
  bgcolor?: string
  /** Text color of each tag */
  color?: string
  /**
   * Text shows on the left of the component
   * @default `Showing:`
   */
  label?: string
  /**
   * Button text that shows on the right of the component for clearing all filters
   * @default `CLEAR ALL`
   */
  clearButtonLabel?: string
  /** All tags will be in readonly mode when `true` */
  readonly?: boolean
  /** Tooltip displaying full text when `true` */
  showTooltip?: boolean
  tooltip?: string
  canClear?: boolean

  /** Callback when a tag is removed */
  onRemove?(
    filters: (FilterTag | string)[],
    removedFilter: (FilterTag | string) | (FilterTag | string)[]
  ): void

  /** Callback when clear button is clicked */
  onClear?(): void
}

export const FilterTags = ({
  label = 'Showing:',
  clearButtonLabel = 'CLEAR ALL',
  tags = [],
  fullWidthTags = false,
  onRemove = () => null,
  onClear = () => null,
  canClear = true,
  removeIcon = <IconX />,
  showTooltip = true,
  readonly = false,
  ...otherProps
}: IFilterTagsProps) => {
  const formatToDisplayableFilterTag = (tag): BaseFilterTag => {
    const { bgcolor, color } = otherProps

    return {
      label: tag.label || '',
      bgcolor: tag.bgcolor || bgcolor,
      color: tag.color || color,
      readonly: tag.readonly || readonly,
      showTooltip: tag.showTooltip || showTooltip,
      tooltip: tag.tooltip || tag.label,
    }
  }

  const renderTagWithTooltip = (
    {
      tooltip,
      showTooltip: displayTooltip,
      readonly: isReadonly,
      label: tagLabel,
      bgcolor,
      color,
      ...otherTagProps
    }: BaseFilterTag,
    remove: ControlledListContext<FilterTag | string>['remove'],
    index: number
  ) => {
    return (
      <WithTooltip
        key={index}
        title={tooltip}
        placement='top'
        isDisabled={!displayTooltip}
      >
        {isReadonly ? (
          <Tag
            bgcolor={bgcolor}
            color={color}
            aria-label={tooltip}
            fullWidth={fullWidthTags}
          >
            {tagLabel}
          </Tag>
        ) : (
          <Tag
            onClick={() => remove(index)}
            icon={removeIcon}
            bgcolor={bgcolor}
            color={color}
            aria-label={tooltip}
            fullWidth={fullWidthTags}
          >
            {tagLabel}
          </Tag>
        )}
      </WithTooltip>
    )
  }

  return (
    <FilterTagsStyled {...otherProps} aria-label='Content is filtered by'>
      <Space direction='horizontal' align='center'>
        <SubText bold>{label}</SubText>
        <ControlledList<FilterTag | string> onChange={onRemove} items={tags}>
          {({ items, remove, clear }) => {
            return (
              <Space
                direction='horizontal'
                align='center'
                size='small'
                flexWrap
              >
                {items.map((item, index) => {
                  return renderTagWithTooltip(
                    formatToDisplayableFilterTag(item),
                    remove,
                    index
                  )
                })}
                {canClear && (
                  <TextButton
                    onClick={() => {
                      clear()
                      onClear()
                    }}
                  >
                    {clearButtonLabel}
                  </TextButton>
                )}
              </Space>
            )
          }}
        </ControlledList>
      </Space>
    </FilterTagsStyled>
  )
}

FilterTags.displayName = 'FilterTags'
